import React, { useState } from 'react';
import Button3D from '../../ui/Button3D';
import Switch from '../../ui/Switch';
import { checklistFromText, checklistToText, kitIcon, settingsSubtitle } from '../eventSettings';
import { DEFAULT_EVENT_WORDS } from '../eventWords';
import SettingsPopup, { Field } from './SettingsPopup';

// Module 19 (BL-380) G8 - "Event Types": the organization's own types (starter or added). Columns shown are the ones event_type_kits backs
// today: Type (icon and name) and Checklist (task count), with Edit, order and on/off. Starter timeline, RSVP form and Default crew are not
// stored per type yet (Q-040), so they are not shown. "Restore starter kits" re-adds a missing starter and never touches an existing one.
function EditForm({ kit, busy, onSave, onBack, words = DEFAULT_EVENT_WORDS }) {
    const [form, setForm] = useState({ label: kit?.label || '', description: kit?.description || '', checklist: checklistToText(kit?.default_checklist) });
    const [problem, setProblem] = useState('');
    const set = (name) => (event) => setForm((previous) => ({ ...previous, [name]: event.target.value }));
    const submit = (event) => {
        event?.preventDefault?.();
        if (!form.label.trim()) return setProblem('Give the type a name.');
        if (form.label.trim().length > 60) return setProblem('Keep the name to 60 characters or fewer.');
        setProblem('');
        onSave({ label: form.label.trim(), description: form.description.trim() || null, default_checklist: checklistFromText(form.checklist) });
    };
    return (
        <form onSubmit={submit} noValidate data-testid="event-type-edit">
            <ul className="ia-resv-grid" aria-label={kit ? `Edit ${kit.label}` : `Add a type of ${words.eventLower}`}>
                <Field id="kit-label" label="Name" wide><input id="kit-label" value={form.label} onChange={set('label')} autoComplete="off" /></Field>
                <Field id="kit-desc" label="Description" wide><textarea id="kit-desc" rows={2} value={form.description} onChange={set('description')} /></Field>
                <Field id="kit-checklist" label="Default checklist" wide hint={`One task per line. A new ${words.eventLower} of this type starts with these as editable notes.`}>
                    <textarea id="kit-checklist" rows={7} value={form.checklist} onChange={set('checklist')} />
                </Field>
            </ul>
            {problem && <p className="ia-resv-error" role="alert">{problem}</p>}
            <div className="mt-3 flex flex-wrap gap-2">
                <Button3D onClick={onBack} disabled={busy}>Back to types</Button3D>
                <Button3D variant="go" onClick={submit} disabled={busy}>{busy ? 'Saving…' : kit ? 'Save type' : 'Add type'}</Button3D>
            </div>
        </form>
    );
}

export default function EventTypesSettingsPopup({ kits, busy, error, notice, onToggle, onMove, onSaveKit, onAdd, onRestore, onClose, words = DEFAULT_EVENT_WORDS }) {
    const [editing, setEditing] = useState(null); // null | 'new' | kit
    const finish = async (promise) => { const ok = await promise; if (ok !== false) setEditing(null); };

    return (
        <SettingsPopup
            title="Event Types"
            subtitle={settingsSubtitle(words)}
            size="wide"
            tileKey="event-types"
            testId="events-settings-types"
            onClose={onClose}
            actions={<Button3D variant="go" onClick={onClose}>Done</Button3D>}
        >
            {editing ? (
                <EditForm
                    words={words}
                    kit={editing === 'new' ? null : editing}
                    busy={busy}
                    onBack={() => setEditing(null)}
                    onSave={(values) => finish(editing === 'new' ? onAdd(values) : onSaveKit(editing, values))}
                />
            ) : (
                <>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                        <Button3D variant="go" onClick={() => setEditing('new')} disabled={busy}>+ Add type</Button3D>
                        <Button3D onClick={onRestore} disabled={busy}>Restore starter kits</Button3D>
                    </div>
                    <div role="table" aria-label="Event types" className="ia-resv-field ia-resv-field--wide" data-testid="event-types-table">
                        <div role="row" className="hidden sm:grid sm:grid-cols-[minmax(0,2fr)_90px_minmax(0,2fr)] gap-3 pb-1 text-xs font-bold uppercase tracking-wide">
                            <span role="columnheader">Type</span><span role="columnheader">Checklist</span><span role="columnheader">Manage</span>
                        </div>
                        {kits.length === 0 && <p className="text-sm" role="row">No {words.eventLower} types yet. Add one, or restore the starter kits.</p>}
                        {kits.map((kit, index) => (
                            <div key={kit.id} role="row" data-testid="event-type-row" className="grid grid-cols-1 gap-2 border-t border-[#3B4D73] py-2 sm:grid-cols-[minmax(0,2fr)_90px_minmax(0,2fr)] sm:items-center sm:gap-3">
                                <span role="cell" className="flex min-w-0 items-center gap-2">
                                    <span aria-hidden="true" className="text-xl">{kitIcon(kit.key)}</span>
                                    <span className="min-w-0 break-words font-bold">{kit.label}</span>
                                    {!kit.is_active && <span className="rounded-full border border-[#94A3B8] px-2 text-xs">Off</span>}
                                </span>
                                <span role="cell" className="text-sm">{(kit.default_checklist || []).length} {(kit.default_checklist || []).length === 1 ? 'task' : 'tasks'}</span>
                                <span role="cell" className="flex flex-wrap items-center gap-2">
                                    <Button3D variant="pill" aria-label={`Move ${kit.label} up`} onClick={() => onMove(kit, 'up')} disabled={busy || index === 0}>▲</Button3D>
                                    <Button3D variant="pill" aria-label={`Move ${kit.label} down`} onClick={() => onMove(kit, 'down')} disabled={busy || index === kits.length - 1}>▼</Button3D>
                                    <Switch checked={kit.is_active} onChange={(value) => onToggle(kit, value)} aria-label={`${kit.label} is on`} onText="On" offText="Off" disabled={busy} />
                                    <Button3D variant="pill" onClick={() => setEditing(kit)} disabled={busy}>Edit</Button3D>
                                </span>
                            </div>
                        ))}
                    </div>
                    {notice && <p role="status" className="mt-2 text-sm" data-testid="event-types-notice">{notice}</p>}
                </>
            )}
            {error && <p className="ia-resv-error" role="alert">{error}</p>}
        </SettingsPopup>
    );
}
